import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Navigate, Routes, Route } from "react-router-dom";
import { AuthProvider } from "@/contexts/AuthContext";
import { DemoModeProvider, isSurgeonConsoleHomeHost } from "@/contexts/DemoModeContext";
import { DemoPathProvider } from "@/contexts/DemoPathContext";
import { SchedulerProvider } from "@/contexts/SchedulerContext";
import { DemoModeBadge } from "@/components/ui/DemoModeBadge";
import { ScrollToTop } from "@/components/ScrollToTop";
import { ProtectedRoute } from "@/components/auth/ProtectedRoute";
import { AppLayout } from "@/components/layout/AppLayout";
import { Layout } from "@/components/Layout";
import { HipaaSessionTimeout } from "@/components/security/HipaaSessionTimeout";
import { SchedulerModal } from "@/components/scheduler/SchedulerModal";
import { GlobalCTAHandler } from "@/components/scheduler/GlobalCTAHandler";
// Public pages
import Landing from "./pages/Landing";
import Platform from "./pages/Platform";
import Solutions from "./pages/Solutions";
import Company from "./pages/Company";
import Resources from "./pages/Resources";
import Auth from "./pages/Auth";
import ResetPassword from "./pages/ResetPassword";
import PANavigator from "./pages/PANavigator";
import BookingPage from "./pages/BookingPage";
import NotFound from "./pages/NotFound";

// App pages (legacy)
import Dashboard from "./pages/Dashboard";
import ClaimsTracker from "./pages/ClaimsTracker";
import PayerIntelligence from "./pages/PayerIntelligence";
import PatientConcierge from "./pages/PatientConcierge";
import Settings from "./pages/Settings";

// Surgeon Console
import SurgeonConsole from "./pages/SurgeonConsole";
import SMARTLaunch from "./pages/SMARTLaunch";
import DictationSession from "./pages/DictationSession";

// LINK Ops
import OpsQueue from "./pages/OpsQueue";
import RulesEngine from "./pages/RulesEngine";
import Integrations from "./pages/Integrations";

// Admin
import BWCFeeValidation from "./pages/admin/BWCFeeValidation";
import PayerRulesAdmin from "./pages/admin/PayerRulesAdmin";
import PayerPlansAdmin from "./pages/admin/PayerPlansAdmin";
import EpicIntegrationSettings from "./pages/admin/EpicIntegrationSettings";
import ClientSettingsPage from "./pages/admin/ClientSettingsPage";
import IntegrationsHealthPage from "./pages/admin/IntegrationsHealthPage";
import AuditLogPage from "./pages/admin/AuditLogPage";
import ClientOnboardingWizard from "./pages/admin/ClientOnboardingWizard";
import PayerSetupWizard from "./pages/admin/PayerSetupWizard";
import SurgeonSubsetsAdmin from "./pages/admin/SurgeonSubsetsAdmin";
import Phase3GovernancePage from "./pages/admin/Phase3GovernancePage";
import AuditReportPage from "./pages/admin/AuditReportPage";
import ProcedureCatalogPage from "./pages/admin/ProcedureCatalogPage";
import ProcedureBundlesPage from "./pages/admin/ProcedureBundlesPage";
import AdminOrchestratorPage from "./pages/admin/AdminOrchestratorPage";
import IntegrationSandboxPage from "./pages/admin/IntegrationSandboxPage";
import BulkCptImportPage from "./pages/admin/BulkCptImportPage";
import TaxonomyImportPage from "./pages/admin/TaxonomyImportPage";
import CptPrimaryRankingPage from "./pages/admin/CptPrimaryRankingPage";
import AdminSchedulerPage from "./pages/admin/AdminSchedulerPage";
import UserManagementPage from "./pages/admin/UserManagementPage";
import OnboardingHub from "./pages/OnboardingHub";
import SupplySync from "./pages/SupplySync";
import SurgeonSupplyReview from "./pages/SurgeonSupplyReview";
import GhostSupplyROI from "./pages/GhostSupplyROI";
import IntraOpEvidencePage from "./pages/IntraOpEvidencePage";
import CodingConsole from "./pages/CodingConsole";
const queryClient = new QueryClient();

const HomeRoute = () => {
  const hostname = typeof window !== 'undefined' ? window.location.hostname : '';

  if (isSurgeonConsoleHomeHost(hostname)) {
    return <Navigate to="/surgeon?demo=true" replace />;
  }

  return <Layout><Landing /></Layout>;
};

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <DemoModeProvider>
        <DemoPathProvider>
          <AuthProvider>
            <HipaaSessionTimeout />
          <Toaster />
          <Sonner />
          <BrowserRouter>
          <DemoModeBadge />
          <ScrollToTop />
          <SchedulerProvider>
            <SchedulerModal />
            <GlobalCTAHandler />
          <Routes>
            {/* Public routes */}
            <Route path="/" element={<HomeRoute />} />
            <Route path="/platform" element={<Layout><Platform /></Layout>} />
            <Route path="/solutions" element={<Layout><Solutions /></Layout>} />
            <Route path="/company" element={<Layout><Company /></Layout>} />
            <Route path="/resources" element={<Layout><Resources /></Layout>} />
            <Route path="/auth" element={<Auth />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/pa-navigator" element={<Layout><PANavigator /></Layout>} />
            <Route path="/book" element={<Layout><BookingPage /></Layout>} />
            <Route path="/launch" element={<SMARTLaunch />} />
            
            {/* Surgeon Console - protected behind auth */}
            <Route path="/surgeon" element={<ProtectedRoute allowedRoles={['surgeon']}><SurgeonConsole /></ProtectedRoute>} />
            <Route path="/surgeon/dictate" element={<ProtectedRoute allowedRoles={['surgeon']}><DictationSession /></ProtectedRoute>} />
            <Route path="/surgeon/intra-op-evidence" element={<ProtectedRoute allowedRoles={['surgeon']}><IntraOpEvidencePage /></ProtectedRoute>} />
            <Route path="/dictation" element={<DictationSession />} />
            <Route 
              path="/surgeon/supply-review" 
              element={
                <ProtectedRoute allowedRoles={['surgeon']}>
                  <SurgeonSupplyReview />
                </ProtectedRoute>
              } 
            />
            
            {/* LINK Ops (link_ops and admin roles) */}
            <Route 
              path="/ops" 
              element={
                <ProtectedRoute allowedRoles={['link_ops', 'admin']}>
                  <OpsQueue />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/ops/rules" 
              element={
                <ProtectedRoute allowedRoles={['link_ops', 'admin']}>
                  <RulesEngine />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/ops/integrations" 
              element={
                <ProtectedRoute allowedRoles={['link_ops', 'admin']}>
                  <Integrations />
                </ProtectedRoute>
              } 
            />
            
            {/* Legacy app routes with sidebar layout - kept for backwards compatibility */}
            <Route path="/dashboard" element={<AppLayout><Dashboard /></AppLayout>} />
            <Route path="/claims" element={<AppLayout><ClaimsTracker /></AppLayout>} />
            <Route path="/payer-intelligence" element={<AppLayout><PayerIntelligence /></AppLayout>} />
            <Route path="/concierge" element={<AppLayout><PatientConcierge /></AppLayout>} />
            <Route path="/settings" element={<AppLayout><Settings /></AppLayout>} />
            
            {/* Admin routes */}
            <Route 
              path="/admin/bwc-fees" 
              element={
                <ProtectedRoute allowedRoles={['admin']}>
                  <BWCFeeValidation />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/admin/payer-rules" 
              element={
                <ProtectedRoute allowedRoles={['link_ops', 'admin']}>
                  <PayerRulesAdmin />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/admin/payer-plans" 
              element={
                <ProtectedRoute allowedRoles={['link_ops', 'admin']}>
                  <PayerPlansAdmin />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/admin/integrations/epic" 
              element={
                <ProtectedRoute allowedRoles={['admin']}>
                  <EpicIntegrationSettings />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/admin/client-settings" 
              element={
                <ProtectedRoute allowedRoles={['admin']}>
                  <ClientSettingsPage />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/admin/integrations-health" 
              element={
                <ProtectedRoute allowedRoles={['admin', 'link_ops']}>
                  <IntegrationsHealthPage />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/admin/audit-log" 
              element={
                <ProtectedRoute allowedRoles={['admin', 'link_ops']}>
                  <AuditLogPage />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/admin/onboarding" 
              element={
                <ProtectedRoute allowedRoles={['admin']}>
                  <ClientOnboardingWizard />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/admin/payer-setup" 
              element={
                <ProtectedRoute allowedRoles={['admin', 'link_ops']}>
                  <PayerSetupWizard />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/admin/surgeon-subsets" 
              element={
                <ProtectedRoute allowedRoles={['admin', 'link_ops']}>
                  <SurgeonSubsetsAdmin />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/admin/phase3-governance" 
              element={
                <ProtectedRoute allowedRoles={['admin']}>
                  <Phase3GovernancePage />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/admin/audit-reports" 
              element={
                <ProtectedRoute allowedRoles={['admin', 'link_ops']}>
                  <AuditReportPage />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/admin/procedure-catalog" 
              element={
                <ProtectedRoute allowedRoles={['admin', 'link_ops']}>
                  <ProcedureCatalogPage />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/admin/procedure-bundles" 
              element={
                <ProtectedRoute allowedRoles={['admin', 'link_ops']}>
                  <ProcedureBundlesPage />
                </ProtectedRoute>
              } 
            />
            
            <Route 
              path="/admin/bulk-import" 
              element={
                <ProtectedRoute allowedRoles={['admin', 'link_ops']}>
                  <BulkCptImportPage />
                </ProtectedRoute>
              } 
            />
            
            <Route 
              path="/admin/taxonomy-import" 
              element={
                <ProtectedRoute allowedRoles={['admin']}>
                  <TaxonomyImportPage />
                </ProtectedRoute>
              } 
            />
            
            <Route 
              path="/admin/orchestrator" 
              element={
                <ProtectedRoute allowedRoles={['admin', 'link_ops']}>
                  <AdminOrchestratorPage />
                </ProtectedRoute>
              } 
            />
            
            <Route 
              path="/admin/integration-sandbox" 
              element={
                <ProtectedRoute allowedRoles={['admin', 'link_ops']}>
                  <IntegrationSandboxPage />
                </ProtectedRoute>
              } 
            />
            
            <Route 
              path="/onboarding-hub" 
              element={
                <ProtectedRoute allowedRoles={['admin', 'link_ops']}>
                  <OnboardingHub />
                </ProtectedRoute>
              } 
            />
            
            <Route 
              path="/ops/supply-sync" 
              element={
                <ProtectedRoute allowedRoles={['admin', 'link_ops']}>
                  <SupplySync />
                </ProtectedRoute>
              } 
            />
            
            <Route 
              path="/ops/ghost-supply-roi" 
              element={
                <ProtectedRoute allowedRoles={['admin', 'link_ops']}>
                  <GhostSupplyROI />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/admin/scheduler" 
              element={
                <ProtectedRoute allowedRoles={['admin']}>
                  <AdminSchedulerPage />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/admin/rules/cpt-primary-ranking" 
              element={
                <ProtectedRoute allowedRoles={['admin', 'link_ops']}>
                  <CptPrimaryRankingPage />
                </ProtectedRoute>
              } 
            />
            <Route
              path="/admin/users"
              element={
                <ProtectedRoute allowedRoles={['admin']}>
                  <UserManagementPage />
                </ProtectedRoute>
              }
            />
            <Route path="/coding-console" element={<CodingConsole />} />
            <Route path="*" element={<NotFound />} />
          </Routes>
          </SchedulerProvider>
            </BrowserRouter>
          </AuthProvider>
        </DemoPathProvider>
      </DemoModeProvider>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
